/**
 * Estado da sessão no cliente.
 *
 * `status` começa em `unknown` de propósito: entre abrir o app e a resposta do
 * `/me`, não sabemos se há sessão. Tratar `unknown` como `anonymous` piscaria a
 * tela de login para quem está logado; tratar como `authenticated` mostraria
 * uma casca de app para quem não está. O splash cobre exatamente essa janela.
 */

import type { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';

import { useFila } from '../features/memorias/fila';
import { clearItinerary, localDb } from '../features/roteiro/persistence';
import { clearLocalData, requestServiceWorkerCacheClear } from '../session/clearLocalData';
import { useSyncStore } from '../sync/store';
import * as api from './api';
import { desligarAlertas } from '../features/alertas/push';
import { performLogout } from './logout';

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: api.SessionUser | null;
  /** Pendências que impediram a saída. Alimenta a folha de confirmação. */
  pendingOnLogout: number | null;
  /**
   * Quantas pendências da conta ANTERIOR foram descartadas ao entrar outra
   * conta neste aparelho. Nunca fica em silêncio: a tela informa.
   */
  discardedOnSwitch: number | null;
  lastError: string | null;

  refresh: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<boolean>;
  /** Devolve `false` quando há pendências e a saída foi barrada. */
  signOut: (options?: { discardPending?: boolean }) => Promise<boolean>;
  dismissPendingPrompt: () => void;
  dismissSwitchNotice: () => void;
  /** Chamado após criar conta por convite — a sessão já vem no cookie. */
  adopt: (user: api.SessionUser) => void;
}

/**
 * O `QueryClient` é injetado em vez de importado: a store precisa limpar o
 * cache no logout, e importar a instância criaria um ciclo com o `main.tsx`.
 */
let queryClient: QueryClient | null = null;
export function bindQueryClient(client: QueryClient): void {
  queryClient = client;
}

async function limparTudo(force: boolean): Promise<void> {
  const db = await localDb();

  await clearLocalData(
    'logout',
    {
      // Tudo que ainda está no outbox é trabalho não sincronizado — mutação
      // enviada com sucesso sai da fila. Conflitos contam junto: também são
      // alterações que o servidor ainda não tem.
      countPendingMutations: async () => (await db.outbox.list()).length,
      clearOutbox: async () => {
        for (const mutacao of await db.outbox.list()) await db.outbox.remove(mutacao.mutationId);
      },
      clearLocalCache: async () => {
        await clearItinerary();
        // A fila de fotos é da conta: nada de outra pessoa fica no aparelho.
        await useFila.getState().esvaziar();
      },
      clearQueryCache: () => queryClient?.clear(),
      clearServiceWorkerCaches: () => requestServiceWorkerCacheClear(),
      clearScopedStorage: () => {
        // O tema é do aparelho, não da conta: sobrevive. Tudo que é escopado
        // à sessão sai.
        for (const chave of Object.keys(localStorage)) {
          if (chave.startsWith('nv:session:') || chave.startsWith('nv:trip:')) {
            localStorage.removeItem(chave);
          }
        }
      },
    },
    { force },
  );

  useSyncStore.setState({ pending: 0, conflicts: 0, conflicted: [], status: 'idle' });
}

const CHAVE_ULTIMA_CONTA = 'nv:session:userId';
const CHAVE_SESSAO_CONHECIDA = 'nv:session:user';
const CHAVE_SAIDA_PENDENTE = 'nv:session:saidaPendente';

/**
 * Saída que não pôde ser revogada no servidor.
 *
 * Sair sem rede tem um efeito contraintuitivo: o cookie de sessão é
 * `httpOnly`, então o JavaScript **não consegue apagá-lo** — quem apaga é o
 * `Set-Cookie` da resposta do `sign-out`, que não chegou. Sem esta marca, o
 * `/me` da próxima abertura responderia 200 e a pessoa voltaria "logada"
 * sozinha, depois de ter saído. Um logout que se desfaz é pior do que um
 * logout que falha em voz alta.
 *
 * Enquanto a marca existir, o app se considera deslogado e insiste em revogar
 * a cada oportunidade de rede.
 */
function marcarSaidaPendente(pendente: boolean): void {
  try {
    if (pendente) localStorage.setItem(CHAVE_SAIDA_PENDENTE, '1');
    else localStorage.removeItem(CHAVE_SAIDA_PENDENTE);
  } catch {
    /* storage indisponível: resta o estado em memória. */
  }
}

function temSaidaPendente(): boolean {
  try {
    return localStorage.getItem(CHAVE_SAIDA_PENDENTE) === '1';
  } catch {
    return false;
  }
}

/**
 * Pista de sessão para funcionar offline.
 *
 * Não é credencial e não autoriza nada — o cookie continua sendo a sessão, e
 * o servidor continua decidindo em toda requisição. É só o que permite ao app
 * se desenhar como "logado" quando `/me` não pôde ser consultado por falta de
 * rede. Sem isso, reabrir a PWA no avião jogaria a pessoa na tela de login com
 * a sessão intacta no cookie — que é a pior forma possível de dizer "sem
 * conexão".
 *
 * Sai no logout, junto com o resto do que é escopado à conta.
 */
function lerPista(): api.SessionUser | null {
  try {
    const bruto = localStorage.getItem(CHAVE_SESSAO_CONHECIDA);
    return bruto ? (JSON.parse(bruto) as api.SessionUser) : null;
  } catch {
    return null;
  }
}

function gravarPista(user: api.SessionUser | null): void {
  try {
    if (user) localStorage.setItem(CHAVE_SESSAO_CONHECIDA, JSON.stringify(user));
    else localStorage.removeItem(CHAVE_SESSAO_CONHECIDA);
  } catch {
    // Modo privado do Safari pode recusar a escrita. O app segue: só perde a
    // capacidade de abrir offline já autenticado.
  }
}

/**
 * Troca de conta no mesmo aparelho.
 *
 * O IndexedDB não é por usuário — é do site. Entrar com outra conta sem apagar
 * exporia a viagem da anterior e, pior, faria o outbox dela ser enviado em
 * nome de quem entrou agora. Aqui a limpeza é forçada, e o número de
 * pendências descartadas volta para a tela informar. Descartar é a regra;
 * descartar **em silêncio** é que não pode acontecer.
 */
async function limparSeTrocouDeConta(userId: string): Promise<number> {
  const anterior = localStorage.getItem(CHAVE_ULTIMA_CONTA);
  localStorage.setItem(CHAVE_ULTIMA_CONTA, userId);

  if (!anterior || anterior === userId) return 0;

  const db = await localDb();
  const pendentes = (await db.outbox.list()).length;
  await limparTudo(true);
  localStorage.setItem(CHAVE_ULTIMA_CONTA, userId);
  return pendentes;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,
  pendingOnLogout: null,
  discardedOnSwitch: null,
  lastError: null,

  refresh: async () => {
    // Uma saída pendente vem antes de qualquer coisa: enquanto o servidor não
    // confirmar a revogação, a sessão do cookie não vale para este app.
    if (temSaidaPendente()) {
      try {
        await api.signOut();
        marcarSaidaPendente(false);
      } catch {
        /* ainda sem rede; tenta de novo na próxima oportunidade. */
      }
      gravarPista(null);
      set({ status: 'anonymous', user: null });
      return;
    }

    try {
      const sessao = await api.fetchMe();
      // 401 é resposta do servidor: a sessão realmente acabou.
      gravarPista(sessao?.user ?? null);
      set(
        sessao
          ? { status: 'authenticated', user: sessao.user, lastError: null }
          : { status: 'anonymous', user: null },
      );
    } catch {
      // Falha de REDE não é ausência de sessão. Quem já estava dentro continua
      // dentro, com a pista local; quem nunca entrou vai para o login.
      const pista = lerPista();
      set((estado) =>
        pista
          ? { status: 'authenticated', user: estado.user ?? pista }
          : estado.status === 'unknown'
            ? { status: 'anonymous' }
            : estado,
      );
    }
  },

  signIn: async (email, password) => {
    try {
      const { user } = await api.signIn(email, password);
      // Entrar de novo resolve a pendência: a sessão antiga foi substituída.
      marcarSaidaPendente(false);
      const descartadas = await limparSeTrocouDeConta(user.id);
      gravarPista(user);
      set({
        status: 'authenticated',
        user,
        lastError: null,
        pendingOnLogout: null,
        discardedOnSwitch: descartadas > 0 ? descartadas : null,
      });
      return true;
    } catch (erro) {
      set({ lastError: erro instanceof Error ? erro.message : 'Não foi possível entrar.' });
      return false;
    }
  },

  signOut: async (options = {}) => {
    const db = await localDb();

    const resultado = await performLogout(
      {
        countPendingMutations: async () => (await db.outbox.list()).length,
        revokeServerSession: async () => {
          // A assinatura de push é deste aparelho E desta conta: quem logar
          // aqui depois não pode receber os avisos de quem saiu.
          await desligarAlertas().catch(() => undefined);
          await api.signOut();
        },
        clearLocalData: ({ force }) => limparTudo(force),
      },
      options,
    );

    if (resultado.kind === 'blocked_by_pending') {
      set({ pendingOnLogout: resultado.pending });
      return false;
    }

    gravarPista(null);
    // Offline, o cookie continua no navegador e fora do alcance do JavaScript.
    // A marca garante que ele não ressuscite a sessão na próxima abertura.
    marcarSaidaPendente(!resultado.serverSessionRevoked);
    set({ status: 'anonymous', user: null, pendingOnLogout: null, lastError: null });
    return true;
  },

  dismissPendingPrompt: () => set({ pendingOnLogout: null }),
  dismissSwitchNotice: () => set({ discardedOnSwitch: null }),

  adopt: (user) => {
    // A pista só vai depois da limpeza: `clearScopedStorage` apaga tudo que
    // começa com `nv:session:`, e gravar antes seria gravar para o lixo.
    void limparSeTrocouDeConta(user.id).then(() => gravarPista(user));
    set({ status: 'authenticated', user, lastError: null });
  },
}));

/**
 * Marca a sessão como encerrada quando o servidor devolve 401.
 *
 * É o outro lado da revogação: a sessão pode acabar sem o app pedir. Não limpa
 * o IndexedDB aqui — um 401 pode ser transitório, e apagar dados por causa de
 * uma resposta isolada seria destrutivo demais. A limpeza acontece no logout
 * explícito e ao entrar outra conta.
 */
export function onUnauthorized(): void {
  const { status } = useAuthStore.getState();
  if (status === 'authenticated') {
    gravarPista(null);
    useAuthStore.setState({ status: 'anonymous', user: null });
  }
}
